/**
 * shared/analytics-types.ts
 *
 * Type definitions for the analytical data layer: fact tables, aggregates,
 * query context, filters, ETL metadata, and retention constants.
 *
 * Spec: docs/superpowers/specs/2026-04-14-wave3-items15-16-anomaly-dashboards.md
 * Sections 1 and 4.
 */

// ---------------------------------------------------------------------------
// Table name constants
// ---------------------------------------------------------------------------
export const FACT_TABLES = [
  "fact_verification_events",
  "fact_audit_events",
  "fact_anomaly_events",
  "fact_org_activity",
  "fact_api_usage",
  "fact_alert_clusters",
  "fact_cross_org_alerts",
] as const;
export type FactTable = (typeof FACT_TABLES)[number];

export const AGGREGATE_TABLES = [
  "agg_org_daily",
  "agg_platform_daily",
] as const;
export type AggregateTable = (typeof AGGREGATE_TABLES)[number];

// ---------------------------------------------------------------------------
// Retention (days, -1 = indefinite)
// ---------------------------------------------------------------------------
export const RETENTION_DAYS: Record<FactTable | AggregateTable, number> = {
  fact_verification_events: 365,
  fact_audit_events: 2555,       // 7 years
  fact_anomaly_events: 365,
  fact_org_activity: 365,
  fact_api_usage: 180,           // 6 months
  fact_alert_clusters: 365,
  fact_cross_org_alerts: 365,
  agg_org_daily: 1095,           // 3 years
  agg_platform_daily: -1,        // indefinite
};

// ---------------------------------------------------------------------------
// Time column per table — the one place that names it. Retention purges on it;
// the query layer filters date ranges and orders on it. `as const satisfies`
// makes a misspelt or missing table a compile error and keeps each value a
// literal.
// ---------------------------------------------------------------------------
export const TIMESTAMP_COLUMN = {
  fact_verification_events: "timestamp",
  fact_audit_events: "timestamp",
  fact_anomaly_events: "timestamp",
  fact_org_activity: "timestamp",
  fact_api_usage: "timestamp",
  fact_alert_clusters: "created_at",
  fact_cross_org_alerts: "created_at",
  agg_org_daily: "day",
  agg_platform_daily: "day",
} as const satisfies Record<FactTable | AggregateTable, string>;

// ---------------------------------------------------------------------------
// ETL constants
// ---------------------------------------------------------------------------
export const ETL_CYCLE_MS = 300_000;

// ---------------------------------------------------------------------------
// Query roles
// ---------------------------------------------------------------------------
export const ANALYTICS_ROLES = ["org_admin", "strem_admin"] as const;
export type AnalyticsRole = (typeof ANALYTICS_ROLES)[number];

// ---------------------------------------------------------------------------
// Query context
// ---------------------------------------------------------------------------
export interface QueryContext {
  role: AnalyticsRole;
  orgId?: string;
}

// ---------------------------------------------------------------------------
// Query filter / sort / pagination
// ---------------------------------------------------------------------------
export interface QueryFilter {
  dateFrom?: string;
  dateTo?: string;
  severity?: string;
  eventType?: string;
  search?: string;
  aalLevel?: string;
  status?: string;
  actorHash?: string;
}

export const QUERY_SORT_DIRECTIONS = ["asc", "desc"] as const;
export type QuerySortDirection = (typeof QUERY_SORT_DIRECTIONS)[number];

/**
 * Requested ordering. Direction only: the order COLUMN is never request input —
 * the query layer fixes it per table (`orderBySql`).
 */
export interface QuerySort {
  direction: QuerySortDirection;
}

export const QUERY_MAX_PAGE_SIZE = 500;
export const QUERY_DEFAULT_PAGE_SIZE = 50;

export interface QueryPage {
  offset: number;
  limit: number;
}

// ---------------------------------------------------------------------------
// Paginated result wrapper
// ---------------------------------------------------------------------------
export interface PaginatedResult<T> {
  rows: T[];
  total: number;
  offset: number;
  limit: number;
}

// ---------------------------------------------------------------------------
// ETL run metadata
// ---------------------------------------------------------------------------
export interface EtlRunMetadata {
  runId: string;
  startedAt: string;
  completedAt: string;
  durationMs: number;
  rowsExtracted: Record<FactTable, number>;
  rowsPurged: Record<FactTable | AggregateTable, number>;
  errors: string[];
}

// ---------------------------------------------------------------------------
// Fact table row types
// ---------------------------------------------------------------------------
export interface FactVerificationEvent {
  id: string;
  orgId: string;
  userHash: string;
  aalLevel: string;
  outcome: string;
  durationMs: number;
  certificateHash: string;
  timestamp: string;
  extractedAt: string;
}

export interface FactAuditEvent {
  id: string;
  orgId: string;
  actorHash: string;
  eventType: string;
  severity: string;
  contextHash: string;
  narrativeText: string;
  sourceEventIds: string[];
  certificateHash: string;
  timestamp: string;
  extractedAt: string;
}

export interface FactAnomalyEvent {
  id: string;
  orgId: string;
  userHash: string;
  riskScore: number;
  signals: string[];
  challengeTier: string;
  outcome: string;
  alertClusterId: string | null;
  sourceEventIds: string[];
  timestamp: string;
  extractedAt: string;
}

export interface FactOrgActivity {
  id: string;
  orgId: string;
  actionType: string;
  actorHash: string;
  targetHash: string | null;
  certificateHash: string;
  timestamp: string;
  extractedAt: string;
}

export interface FactApiUsage {
  id: string;
  orgId: string;
  endpoint: string;
  method: string;
  statusCode: number;
  latencyMs: number;
  timestamp: string;
  extractedAt: string;
}

export interface FactAlertCluster {
  id: string;
  orgId: string;
  severity: string;
  memberAlertIds: string[];
  triggerSignals: string[];
  createdAt: string;
  resolvedAt: string | null;
  resolutionType: string | null;
  extractedAt: string;
}

export interface FactCrossOrgAlert {
  id: string;
  userHash: string;
  orgIds: string[];
  perOrgRiskScores: Record<string, number>;
  correlationType: string;
  createdAt: string;
  extractedAt: string;
}

// ---------------------------------------------------------------------------
// Aggregate row types
// ---------------------------------------------------------------------------
export interface AggOrgDaily {
  id: string;
  orgId: string;
  day: string;
  memberCount: number;
  newMembers: number;
  verificationRate: number;
  anomalyCount: number;
  auditEventCount: number;
  avgVerificationDurationMs: number;
  aalDistribution: Record<string, number>;
  computedAt: string;
}

export interface AggPlatformDaily {
  id: string;
  day: string;
  totalOrgs: number;
  totalMembers: number;
  totalVerifications: number;
  totalAnomalies: number;
  avgVerificationRate: number;
  activeApiOrgs: number;
  computedAt: string;
}
